import type { FC } from 'react';

import { ComponentQuery, currentConverter, powerConverter, voltageConverter } from 'models';
import { FormatHelpers } from 'helpers/formatters';

import { CurrentField } from 'components/component-fields/CurrentField';
import { PowerField } from 'components/component-fields/PowerField';
import { VoltageField } from 'components/component-fields/VoltageField';
import { useComponentSearchFilters } from 'components/component-overview/hooks/use-component-search-filters';
import { InlineFilters } from 'components/component-overview/inline-filters/InlineFilters';
import { SearchService } from 'components/component-overview/services/SearchService';

type RatingFilter = keyof NonNullable<ComponentQuery['ratings']>;

const InlineFiltersRating: FC<{ rating: RatingFilter }> = ({ rating }) => {
    const { ratings } = useComponentSearchFilters();
    const min = ratings?.[rating]?.min;

    if (!ratings?.[rating]) return null;

    const { format, Field, placeholder } = RATING_FILTERS[rating];
    const label = min ? `≥ ${format(min)}` : undefined;

    return (
        <InlineFilters.Section
            body={
                <InlineFilters.MeasurementWrapper label={label} placeholder={placeholder}>
                    <Field name={`ratings.${rating}`} size="xs" fields={['min']} hideIcons />
                </InlineFilters.MeasurementWrapper>
            }
            onRemove={() => SearchService.setFilter('ratings', { ...ratings, [rating]: undefined })}
            active
        />
    );
};

const RATING_FILTERS = {
    power: {
        format: (value: number) => FormatHelpers.formatValue(value, powerConverter),
        Field: PowerField,
        placeholder: 'Power',
    },
    voltage: {
        format: (value: number) => FormatHelpers.formatValue(value, voltageConverter),
        Field: VoltageField,
        placeholder: 'Voltage',
    },
    current: {
        format: (value: number) => FormatHelpers.formatValue(value, currentConverter),
        Field: CurrentField,
        placeholder: 'Current',
    },
};

export { InlineFiltersRating };
